import { Component, inject, Input, OnInit, signal } from '@angular/core';
import { CategoryStore } from '../../../stores/category/category.state';
import { POI } from '../../../models/data-layer-type';
import { AccordionModule } from 'primeng/accordion';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { InputTextModule } from 'primeng/inputtext';
import { KeyValuePipe, NgIf } from '@angular/common';
import { ImportPoiValidationResult } from '../../../models/import-data';

@Component({
  selector: 'app-poi-import-list',
  standalone: true,
  imports: [
    AccordionModule,
    IconFieldModule,
    InputIconModule,
    InputTextModule,
    KeyValuePipe,
    NgIf,
  ],
  templateUrl: './poi-import-list.component.html',
  styleUrl: './poi-import-list.component.scss',
})
export class PoiImportListComponent implements OnInit {
  readonly categoryStore = inject(CategoryStore);
  @Input() items: POI[] = [];
  @Input() validationResults: ImportPoiValidationResult | null = null;
  @Input() severity = '';
  @Input() icon = '';
  itemsFiltered: POI[] = [];
  searchQuery = signal<string>('');

  /**
   * Checks if there are any errors at the specified index in the validation results.
   *
   * @param {number} idx - The index to check for errors in the validation results.
   * @return {boolean} Returns true if there are errors at the specified index, otherwise false.
   */
  hasErrors(idx: number) {
    return !!(
      this.validationResults?.errors && this.validationResults?.errors?.[idx]
    );
  }

  /**
   * Determines if there are any warnings at the specified index in the validation results.
   *
   * @param {number} idx - The index to check for warnings within the validation results.
   * @return {boolean} Returns true if there is a warning at the specified index; otherwise, returns false.
   */
  hasWarnings(idx: number) {
    return this.validationResults?.warnings?.[idx] || false;
  }

  /**
   * Lifecycle hook that is called after Angular has initialized all data-bound properties of a directive.
   * It initializes the itemsFiltered array with a shallow copy of the items array.
   *
   * @return {void} This method does not return a value.
   */
  ngOnInit(): void {
    this.itemsFiltered = [...this.items];
  }

  /**
   * Updates the filtered list of items based on the provided search query.
   * If the search query is empty, all items are included in the filtered list.
   *
   * @param {string} searchQuery - The text used to filter the list of items. Comparison is case-insensitive.
   * @return {void} - This method does not return a value.
   */
  updateList(searchQuery: string): void {
    if (!searchQuery.length) {
      this.itemsFiltered = this.items;
    } else {
      this.itemsFiltered = this.items.filter((item: POI) => {
        return item.name?.toLowerCase().includes(searchQuery.toLowerCase());
      });
    }
  }
}
